<template>
  <div class="SidebarInfo">
    <template v-if="authenticated">
      <!--
        Task 12 / STEP-04. Withheld alongside the role label while acting
        context is undecided -- see `roleUnresolved` below. Both avatars are
        role-specific illustrations, and the repo has no neutral equivalent
        sized for this slot, so none is shown rather than one being implied.
      -->
      <component v-if="avatar" class="SidebarInfo-avatar" :is="avatar" />
      <div class="SidebarInfo-name">{{ name }}</div>
      <!--
        Task 12 / STEP-04. Withheld while acting context is genuinely
        undecided -- see `roleUnresolved` below.
      -->
      <div v-if="!roleUnresolved" class="SidebarInfo-type">{{ type }}</div>
      <div class="SidebarInfo-status">
        <div class="SidebarInfo-status-circle" :class="sessionStatus.class" />
        <div class="SidebarInfo-status-text">{{ sessionStatus.text }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import StudentIcon from '@/assets/student-icon.svg'
import VolunteerIcon from '@/assets/volunteer-icon.svg'

export default {
  props: {
    authenticated: Boolean,
    isVolunteer: Boolean,
    name: String,
  },
  components: {
    StudentIcon,
    VolunteerIcon,
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
    }),
    ...mapGetters({
      isSessionAlive: 'user/isSessionAlive',
      activeRole: 'user/activeRole',
      roleSelectionRequired: 'user/roleSelectionRequired',
    }),
    // Task 12 / STEP-04 remediation.
    //
    // `isVolunteer` is the frozen ACTING-CONTEXT alias, so a dual-role user who
    // has not chosen yet (`activeRole === null`) makes it false, and the
    // ternary below asserted "Student" -- on the very screen that is asking
    // which role they want to use. The sidebar answered the question the
    // dashboard was still posing.
    //
    // Acting context has no answer yet, so none is presented. Nothing is
    // defaulted, no role is set, and `isVolunteer` keeps its meaning: this is
    // presentation withheld, not a new role state.
    roleUnresolved() {
      return this.roleSelectionRequired && this.activeRole === null
    },
    type() {
      return this.isVolunteer
        ? this.$root.$t('appSidebar.sidebarInfo.volunteer')
        : this.$root.$t('appSidebar.sidebarInfo.student')
    },
    sessionStatus() {
      const inSession = this.isSessionAlive

      const status = {
        text: this.$root.$t('appSidebar.sidebarInfo.status.default'),
        class: 'SidebarInfo-status-circle',
      }

      if (this.isVolunteer) {
        status.text = this.$root.$t('appSidebar.sidebarInfo.status.isVolunteer')
      }

      if (this.user.isBanned) {
        status.class += '--banned'
        status.text = this.$root.$t(
          'appSidebar.sidebarInfo.status.userIsBanned'
        )
      }

      if (inSession) {
        status.class += '--session'
        status.text = this.$root.$t('appSidebar.sidebarInfo.status.inSession')
      }

      if (this.isVolunteer && !this.user.isOnboarded) {
        status.class += '--onboarding'
        status.text = this.$root.$t('appSidebar.sidebarInfo.status.Onboarding')
      }

      if (this.isVolunteer && this.user.isOnboarded && !this.user.isApproved) {
        status.class += '--onboarding'
        status.text = this.$root.$t(
          'appSidebar.sidebarInfo.status.pendingApproval'
        )
      }

      return status
    },
    // Task 12 / STEP-04. Same acting-context fall-through as `type()` had:
    // `isVolunteer` is false for an undecided dual-role user, so the ternary
    // resolved to the STUDENT illustration. Null means "no role-specific
    // avatar", which the template renders as nothing.
    avatar() {
      if (this.roleUnresolved) return null
      return this.isVolunteer ? VolunteerIcon : StudentIcon
    },
  },
}
</script>

<style lang="scss" scoped>
.SidebarInfo {
  $spacing: 8px;
  @include flex-container(column, $align-items: flex-start);
  @include child-spacing(top, $spacing);

  &-avatar {
    $size: 80px;
    border-radius: 50%;
    width: $size;
    height: $size;
  }

  &-name {
    @include font-category('display-small');
    margin-top: $spacing * 2;
    text-align: left;
  }

  &-type {
    @include font-category('body');
    color: $c-secondary-grey;
  }

  &-status {
    @include flex-container(row, center, baseline);
    @include child-spacing(left, 8px);
    @include font-category('body');

    &-circle {
      $size: 8px;

      border-radius: 50%;
      background: $c-success-green;
      width: $size;
      height: $size;

      &--session,
      &--onboarding {
        background: $c-warning-orange;
      }

      &--banned {
        background-color: $c-banned-grey;
      }
    }
  }
}

html.is-rtl {
  .SidebarInfo {
    &-name {
      text-align: right;
    }

    &-status {
      @include child-spacing-rtl(left, 8px);
    }
  }
}
</style>
